/**
 * HEROs capability matrix — ported verbatim from the prototype `admin-auth.jsx`.
 *
 * This is the canonical role/permission model (PROJECT_BRIEF §2, §4.12).
 * The server derives every authorization decision from `can()`; the web client
 * uses the same `can()` to show/hide/disable UI. Keep this file framework-free
 * (no React, no Node) so both sides import it identically.
 *
 * Grant values:
 *   true        -> full access (allow)
 *   'view'      -> read-only / can see but not mutate
 *   undefined   -> no access (deny)
 */
export const ROLE_DEFS = [
    { id: 'super', name: 'Super Admin', users: 1, color: 'red', desc: 'Full system control including audit, backups, and pricing engine.' },
    { id: 'admin', name: 'Admin Team', users: 1, color: 'red', desc: 'User management, role assignment, pricing configuration, audit logs.' },
    { id: 'pm', name: 'Project Manager', users: 3, color: 'violet', desc: 'Create projects, manage members, edit timelines, build quotations. Acts as the in-system lead for the project.' },
    { id: 'stat', name: 'Statistician', users: 2, color: 'blue', desc: 'Sample size, SAP, primary & secondary analyses, summary reports.' },
    { id: 'dm', name: 'Data Manager', users: 1, color: 'cyan', desc: 'DMP, CRF/eCRF, REDCap build, data cleaning, database lock.' },
    { id: 'ra', name: 'Research Assistant', users: 1, color: 'green', desc: 'Recruitment, follow-up visits, on-site support.' },
    { id: 'monitor', name: 'Monitor', users: 0, color: 'orange', desc: 'On-site monitoring, source data verification.' },
    { id: 'finance', name: 'Finance/Admin Officer', users: 0, color: 'slate', desc: 'View compensation & payments; export ledgers.' },
    { id: 'viewer', name: 'Viewer', users: 0, color: 'slate', desc: 'Read-only access to permitted projects.' },
    { id: 'pi', name: 'Principal Investigator', users: 0, color: 'navy', desc: 'Owns scientific content: approves/locks protocol & SAP, edits study design, allocates compensation on own projects.' },
];
export const CAPS = [
    {
        group: 'System',
        caps: [
            { id: 'sys.users.manage', label: 'Manage user accounts & roles', roles: { super: true, admin: true } },
            { id: 'sys.pricing.edit', label: 'Edit pricing library', roles: { super: true, admin: true } },
            { id: 'sys.audit.view', label: 'View audit logs', roles: { super: true, admin: true, finance: 'view' } },
            { id: 'sys.backup.run', label: 'Run / restore backups', roles: { super: true } },
        ],
    },
    {
        group: 'Projects',
        caps: [
            { id: 'proj.create', label: 'Create new project', roles: { super: true, admin: true, pm: true } },
            { id: 'proj.edit', label: 'Edit project profile & study design', roles: { super: true, admin: true, pm: true, pi: true } },
            { id: 'proj.members', label: 'Add / remove project members', roles: { super: true, admin: true, pm: true } },
            { id: 'proj.timeline.edit', label: 'Edit project timeline (any task)', roles: { super: true, admin: true, pm: true, pi: true } },
            { id: 'proj.timeline.status', label: 'Update status on own tasks', roles: { super: true, admin: true, pm: true, pi: true, stat: true, dm: true, ra: true, monitor: true } },
            { id: 'proj.delete', label: 'Delete or terminate project', roles: { super: true, admin: true } },
        ],
    },
    {
        group: 'Documents',
        caps: [
            { id: 'doc.upload', label: 'Upload documents to project', roles: { super: true, admin: true, pm: true, pi: true, stat: true, dm: true, ra: true } },
            { id: 'doc.approve', label: 'Approve protocol / SAP versions', roles: { super: true, admin: true, pi: true } },
            { id: 'doc.lock', label: 'Lock final version', roles: { super: true, admin: true, pi: true } },
        ],
    },
    {
        group: 'Quotation',
        caps: [
            { id: 'quote.create', label: 'Build & version quotations', roles: { super: true, admin: true, pm: true, pi: true, stat: true } },
            { id: 'quote.adjust', label: 'Manual price adjustment / discount', roles: { super: true, admin: true, pm: true } },
            { id: 'quote.export', label: 'Export quotation as PDF', roles: { super: true, admin: true, pm: true, pi: true, stat: true, dm: true, finance: 'view' } },
        ],
    },
    {
        group: 'Compensation & Payments',
        caps: [
            { id: 'comp.allocate', label: 'Allocate task compensation %', roles: { super: true, admin: true, pm: true, pi: true } },
            { id: 'comp.received.self', label: 'Tick "received" on own line', roles: { super: true, admin: true, pm: true, pi: true, stat: true, dm: true, ra: true, monitor: true } },
            { id: 'comp.received.any', label: 'Override "received" on others’ lines', roles: { super: true, admin: true } },
            { id: 'comp.payments.view', label: 'View unit-wide Payments tracker', roles: { super: true, admin: true, finance: true, pm: 'view' } },
        ],
    },
];
/** Flat lookup built once from CAPS for O(1) `can()` checks. */
const CAP_INDEX = Object.fromEntries(CAPS.flatMap((g) => g.caps).map((c) => [c.id, c.roles]));
/**
 * Pure authorization helper. Returns the access level a role has for a
 * capability. The server MUST treat 'view' as read-only and 'deny' as 403.
 *
 * @example can('pm', 'proj.create') // 'allow'
 * @example can('finance', 'sys.audit.view') // 'view'
 * @example can('viewer', 'proj.delete') // 'deny'
 */
export function can(role, capabilityId) {
    const grant = CAP_INDEX[capabilityId]?.[role];
    if (grant === true)
        return 'allow';
    if (grant === 'view')
        return 'view';
    return 'deny';
}
//# sourceMappingURL=caps.js.map